import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
export function MysteryLayer() {
  const audioContextRef = useRef<AudioContext | null>(null);
  const [audioInitialized, setAudioInitialized] = useState(false);
  // Initialize audio on any user interaction
  useEffect(() => {
    const initAudio = () => {
      if (audioInitialized) return;
      setAudioInitialized(true);
      // Create audio context
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      // Create binaural beat (40Hz - Gamma waves for focus/concentration)
      const leftFreq = 200; // Base frequency
      const rightFreq = 240; // 40Hz difference creates binaural beat
      // Left channel oscillator
      const leftOsc = audioContext.createOscillator();
      leftOsc.frequency.value = leftFreq;
      leftOsc.type = 'sine';
      // Right channel oscillator
      const rightOsc = audioContext.createOscillator();
      rightOsc.frequency.value = rightFreq;
      rightOsc.type = 'sine';
      // Create stereo panner for each channel
      const leftPanner = audioContext.createStereoPanner();
      leftPanner.pan.value = -1; // Full left
      const rightPanner = audioContext.createStereoPanner();
      rightPanner.pan.value = 1; // Full right
      // Create gain nodes for volume control (full volume for testing)
      const leftGain = audioContext.createGain();
      leftGain.gain.value = 1.0; // Maximum volume for testing
      const rightGain = audioContext.createGain();
      rightGain.gain.value = 1.0; // Maximum volume for testing
      // Add subtle low-pass filter for warmth
      const filter = audioContext.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 800;
      // Connect left channel
      leftOsc.connect(leftGain);
      leftGain.connect(leftPanner);
      leftPanner.connect(filter);
      // Connect right channel
      rightOsc.connect(rightGain);
      rightGain.connect(rightPanner);
      rightPanner.connect(filter);
      // Connect to output
      filter.connect(audioContext.destination);
      // Start oscillators
      leftOsc.start();
      rightOsc.start();
    };
    // Auto-start on any user interaction
    const events = ['click', 'touchstart', 'keydown', 'scroll', 'mousemove'];
    events.forEach(event => {
      document.addEventListener(event, initAudio, {
        once: true
      });
    });
    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, initAudio);
      });
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [audioInitialized]);
  // Hidden symbols/glyphs data
  const symbols = [{
    char: 'α',
    top: '10%',
    left: '5%',
    delay: 0
  }, {
    char: 'β',
    top: '25%',
    right: '8%',
    delay: 2
  }, {
    char: 'Ω',
    top: '45%',
    left: '3%',
    delay: 4
  }, {
    char: 'Δ',
    top: '60%',
    right: '5%',
    delay: 1
  }, {
    char: 'Σ',
    top: '75%',
    left: '7%',
    delay: 3
  }, {
    char: 'Φ',
    top: '85%',
    right: '6%',
    delay: 5
  }, {
    char: '⚗',
    top: '15%',
    right: '12%',
    delay: 2.5
  }, {
    char: '⚛',
    top: '35%',
    left: '10%',
    delay: 1.5
  }, {
    char: '∞',
    top: '55%',
    right: '15%',
    delay: 3.5
  }, {
    char: '⚕',
    top: '70%',
    left: '12%',
    delay: 4.5
  }];
  return <>
      {/* Breathing Shadow Overlay */}
      <motion.div className="fixed inset-0 pointer-events-none z-[1]" animate={{
      opacity: [0.3, 0.5, 0.3]
    }} transition={{
      duration: 12,
      repeat: Infinity,
      ease: 'easeInOut'
    }} style={{
      background: 'radial-gradient(circle at 50% 50%, transparent 0%, rgba(0, 0, 0, 0.4) 100%)'
    }} data-id="element-1395" />

      {/* Hidden Symbols/Glyphs */}
      <div className="fixed inset-0 pointer-events-none z-[2] overflow-hidden" data-id="element-1396">
        {symbols.map((symbol, i) => <motion.div key={i} className="absolute text-6xl text-amber-600/5 font-serif select-none" style={{
        top: symbol.top,
        left: symbol.left,
        right: symbol.right,
        fontFamily: 'Cinzel, serif'
      }} animate={{
        opacity: [0.03, 0.08, 0.03],
        scale: [1, 1.05, 1],
        rotate: [0, 5, 0]
      }} transition={{
        duration: 15,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: symbol.delay
      }} data-id="element-1397">
            {symbol.char}
          </motion.div>)}

        {/* Molecular structure patterns */}
        <svg className="absolute top-1/4 left-1/4 w-64 h-64 opacity-[0.02]" viewBox="0 0 100 100" data-id="element-1398">
          <motion.circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-amber-600" animate={{
          scale: [1, 1.1, 1],
          opacity: [0.5, 1, 0.5]
        }} transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-1399" />
          <motion.circle cx="30" cy="30" r="8" fill="currentColor" className="text-amber-600" animate={{
          opacity: [0.3, 0.7, 0.3]
        }} transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1
        }} data-id="element-1400" />
          <motion.circle cx="70" cy="30" r="8" fill="currentColor" className="text-amber-600" animate={{
          opacity: [0.3, 0.7, 0.3]
        }} transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 2
        }} data-id="element-1401" />
          <motion.circle cx="50" cy="70" r="8" fill="currentColor" className="text-amber-600" animate={{
          opacity: [0.3, 0.7, 0.3]
        }} transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 3
        }} data-id="element-1402" />
          <line x1="30" y1="30" x2="50" y2="50" stroke="currentColor" strokeWidth="0.5" className="text-amber-600" data-id="element-1403" />
          <line x1="70" y1="30" x2="50" y2="50" stroke="currentColor" strokeWidth="0.5" className="text-amber-600" data-id="element-1404" />
          <line x1="50" y1="70" x2="50" y2="50" stroke="currentColor" strokeWidth="0.5" className="text-amber-600" data-id="element-1405" />
        </svg>

        {/* DNA helix pattern */}
        <svg className="absolute bottom-1/4 right-1/4 w-48 h-96 opacity-[0.02]" viewBox="0 0 50 200" data-id="element-1406">
          <motion.path d="M 10 0 Q 25 50 10 100 Q -5 150 10 200" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-amber-600" animate={{
          opacity: [0.3, 0.6, 0.3]
        }} transition={{
          duration: 12,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-1407" />
          <motion.path d="M 40 0 Q 25 50 40 100 Q 55 150 40 200" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-amber-600" animate={{
          opacity: [0.3, 0.6, 0.3]
        }} transition={{
          duration: 12,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1
        }} data-id="element-1408" />
        </svg>

        {/* Hexagonal pattern */}
        <svg className="absolute top-1/2 right-1/3 w-32 h-32 opacity-[0.02]" viewBox="0 0 100 100" data-id="element-1409">
          <motion.polygon points="50,10 90,30 90,70 50,90 10,70 10,30" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-amber-600" animate={{
          rotate: [0, 360],
          opacity: [0.3, 0.6, 0.3]
        }} transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-1410" />
        </svg>
      </div>

      {/* Subtle grain/noise texture */}
      <div className="fixed inset-0 pointer-events-none z-[1] opacity-[0.03]" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'repeat'
    }} data-id="element-1411" />
    </>;
}